import React, { useState, useEffect } from 'react';
import { IoSend, IoClose, IoSparklesSharp } from 'react-icons/io5'; 
import { classifyMessageWithMLKit } from '../../utils/mlKitClassifier';
import '../../ThoughtModal.css';

const MOODS = {
  neutral: '#8b5cf6', // Violet
  heavy:   '#94a3b8', // Slate Grey
  hopeful: '#f59e0b', // Amber/Gold
  intense: '#ef4444', // Red
};

const PROMPTS = [
  "Share a thought...",
  "What's happening today?",
  "Signal the community...",
  "Don't be shy..."
];

const ThoughtModal = ({ isOpen, text, setText, honeypot, setHoneypot, onClose, onSubmit, isAnalyzing, maxLength }) => {
  const [activePrompt, setActivePrompt] = useState(PROMPTS[0]);
  const [accentColor, setAccentColor] = useState(MOODS.neutral);
  const [isTransmitting, setIsTransmitting] = useState(false);
  const [mlResult, setMlResult] = useState({
    category: 'General',
    sentiment: 'neutral',
    language: 'English',
    confidence: 1.0,
    icon: '💭'
  });

  useEffect(() => {
    if (isOpen) {
      setActivePrompt(PROMPTS[Math.floor(Math.random() * PROMPTS.length)]);
      setAccentColor(MOODS.neutral);
      setIsTransmitting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    let isMounted = true;
    const runMlKitClassification = async () => {
      const result = await classifyMessageWithMLKit(text);
      if (!isMounted) return;

      setMlResult(result);
      if (result.sentiment === 'distressed') {
        if (result.category.includes('Anger')) setAccentColor(MOODS.intense);
        else setAccentColor(MOODS.heavy);
      } else if (result.sentiment === 'positive') {
        setAccentColor(MOODS.hopeful);
      } else {
        setAccentColor(MOODS.neutral);
      }
    };

    runMlKitClassification();
    return () => { isMounted = false; };
  }, [text]);

  const handleTransmit = () => {
    if (text.trim().length === 0) return;
    setIsTransmitting(true);
    setTimeout(() => {
      onSubmit(mlResult);
    }, 600); 
  };

  if (!isOpen) return null;

  const isProcessing = isAnalyzing || isTransmitting;

  return (
    <div className="simple-modal-overlay" onClick={isProcessing ? undefined : onClose}>
      <div 
        className="simple-modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ borderTopColor: accentColor }}
      >
        
        {/* HEADER: Title + Character Count */}
        <div className="simple-modal-header">
          <span className="modal-title" style={{ color: accentColor }}>
            <IoSparklesSharp style={{ marginRight: '8px' }} />
            {isProcessing ? "Transmitting..." : "New Signal"}
          </span>
          
          <div className="char-count" style={{ color: (maxLength - text.length) < 20 ? '#ef4444' : '#666' }}>
            {maxLength - text.length} / {maxLength}
          </div>
        </div>

        {/* BODY: Text Area or Branded Loading Indicator */}
        <div className="simple-modal-body">
          {isProcessing ? (
            <div className="transmitting-overlay">
              <div className="firefly-loader-orb" style={{ color: accentColor }}>
                {mlResult.icon || '✨'}
              </div>
              <div className="transmitting-text" style={{ color: accentColor }}>
                Aligning signal with the night...
              </div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span className="spinner-ring" style={{ color: accentColor }} />
              </div>
            </div>
          ) : (
            <textarea 
              className="simple-textarea"
              placeholder={activePrompt}
              value={text} 
              onChange={(e) => setText(e.target.value)} 
              autoFocus 
              maxLength={maxLength}
            />
          )}
        </div>

        {/* FOOTER: Cancel + Post Buttons */}
        <div className="simple-modal-footer">
          <button onClick={onClose} disabled={isProcessing} className="simple-cancel-btn">
            <IoClose /> Cancel
          </button>

          <button 
            onClick={handleTransmit} 
            disabled={isProcessing || text.length === 0} 
            className="simple-send-btn"
            style={{ 
              backgroundColor: text.length > 0 ? accentColor : '#333',
              color: text.length > 0 ? '#000' : '#888' 
            }}
          >
            {isProcessing ? (
              <>
                <span className="spinner-ring" style={{ color: '#000', borderWidth: '2px' }} />
                <span>Sending...</span>
              </>
            ) : (
              <><IoSend /> Send</>
            )}
          </button>
        </div>

        <input type="text" style={{ display: 'none' }} value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
      </div>
    </div>
  );
};

export default ThoughtModal;